import BarChartIcon from "@mui/icons-material/BarChart";
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { sumBy } from "lodash";
import type { FC } from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import type { OrdersTrendBucket, OrdersTrendGranularity } from "../statisticsApi";
import { formatBucketLabel, formatEur } from "../statisticsFormat";

const GRANULARITY_LABELS: Record<OrdersTrendGranularity, string> = { DAY: "Dzień", MONTH: "Miesiąc", YEAR: "Rok" };
const BREAKDOWN_LABELS: Record<OrdersTrendGranularity, string> = {
  DAY: "Podział dzienny",
  MONTH: "Podział miesięczny",
  YEAR: "Podział roczny",
};
const BAR_COLOR = "#3557a1";

interface ChartPoint {
  readonly label: string;
  readonly ordersCount: number;
  readonly valueEur: number;
}

interface TooltipPayload {
  readonly payload: ChartPoint;
}

interface BucketTooltipProps {
  readonly active?: boolean;
  readonly payload?: readonly TooltipPayload[];
}

const BucketTooltip: FC<BucketTooltipProps> = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) {
    return null;
  }
  return (
    <Paper sx={{ px: 1.5, py: 1 }}>
      <Typography variant="caption">
        {point.ordersCount} zamówień · {formatEur(point.valueEur)}
      </Typography>
    </Paper>
  );
};

interface Props {
  readonly title: string;
  readonly buckets: OrdersTrendBucket[];
  readonly granularity: OrdersTrendGranularity;
  /** When set, a Dzień / Miesiąc / Rok toggle is shown in the header. */
  readonly onGranularityChange?: (granularity: OrdersTrendGranularity) => void;
}

const OrdersBarChart: FC<Props> = ({ title, buckets, granularity, onGranularityChange }) => {
  const spansYears = new Set(buckets.map((bucket) => bucket.key.slice(0, 4))).size > 1;
  const points: ChartPoint[] = buckets.map((bucket) => ({
    label: formatBucketLabel(bucket.key, granularity, spansYears),
    ordersCount: bucket.ordersCount,
    valueEur: bucket.valueEur,
  }));
  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <BarChartIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: "bold" }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {onGranularityChange ? `Agregacja: ${GRANULARITY_LABELS[granularity]}` : BREAKDOWN_LABELS[granularity]}
            </Typography>
          </Box>
        </Stack>
        {onGranularityChange && (
          <ToggleButtonGroup
            value={granularity}
            exclusive
            size="small"
            aria-label="Agregacja wykresu zamówień"
            onChange={(_, value: OrdersTrendGranularity | null) => value && onGranularityChange(value)}
          >
            {(Object.keys(GRANULARITY_LABELS) as OrdersTrendGranularity[]).map((option) => (
              <ToggleButton key={option} value={option}>
                {GRANULARITY_LABELS[option]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Stack>
      <Box sx={{ height: 220, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 20, right: 8, bottom: 0, left: 8 }}>
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tick={{ fontSize: 11, fill: "rgba(0,0,0,0.6)" }}
            />
            <Tooltip content={<BucketTooltip />} cursor={{ fill: "rgba(53, 87, 161, 0.06)" }} />
            <Bar
              dataKey="ordersCount"
              radius={[6, 6, 2, 2]}
              maxBarSize={granularity === "YEAR" ? 48 : 36}
              minPointSize={6}
            >
              {points.map((point) => (
                <Cell key={point.label} fill={BAR_COLOR} fillOpacity={point.ordersCount === 0 ? 0.25 : 1} />
              ))}
              <LabelList dataKey="ordersCount" position="top" fill={BAR_COLOR} fontSize={12} fontWeight="bold" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Suma zamówień: <strong>{sumBy(buckets, (bucket) => bucket.ordersCount)}</strong>
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Suma wartości: <strong>{formatEur(sumBy(buckets, (bucket) => bucket.valueEur))}</strong>
        </Typography>
      </Stack>
    </Paper>
  );
};

export default OrdersBarChart;
